import { type ReactNode } from 'react';
import type { TeamMember } from '../types';
import { ROLE_DESCRIPTIONS } from '../data/role-descriptions';
import { isActiveStatus } from '../data/active-status';
import { GridLayout, RowLayout } from './role-section-layouts';

type Props = {
  role: string;
  members: TeamMember[];
  compact?: boolean;
  onSelect?: (id: string) => void;
};

const fmtCount = (a: number, t: number): string =>
  a === t ? String(t) : `${a} (${t})`;

function SectionHeader(p: {
  role: string;
  activeCount: number;
  totalCount: number;
}): ReactNode {
  const info = ROLE_DESCRIPTIONS[p.role];
  return (
    <div className="flex items-baseline gap-sm border-b border-white/[0.06] pb-sm">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
        {info?.title ?? p.role}
      </h3>
      <span className="text-[11px] tabular-nums text-text-tertiary/50">
        {fmtCount(p.activeCount, p.totalCount)}
      </span>
      {info?.tagline && (
        <span className="text-[11px] text-text-tertiary">{info.tagline}</span>
      )}
    </div>
  );
}

function Body(p: Props & { isPre: boolean }): ReactNode {
  if (p.isPre)
    return (
      <RowLayout
        members={p.members}
        {...(p.onSelect && { onSelect: p.onSelect })}
      />
    );
  return (
    <GridLayout
      members={p.members}
      compact={p.compact ?? false}
      {...(p.onSelect && { onSelect: p.onSelect })}
    />
  );
}

export function RoleSection(p: Props): ReactNode {
  if (p.members.length === 0) return null;
  const isPre = !!p.compact && p.role === 'pre_collectors';
  const activeCount = p.members.filter(isActiveStatus).length;
  return (
    <section>
      <SectionHeader
        role={p.role}
        activeCount={activeCount}
        totalCount={p.members.length}
      />
      <Body {...p} isPre={isPre} />
    </section>
  );
}
